/**
 * useOnboardingMark.
 *
 * Convenience hook for components that should signal "this onboarding
 * step has been completed" when they mount or when a relevant action
 * succeeds. Wraps the markOnboardingStep mutation with sensible
 * defaults:
 *   - idempotent: server already de-dupes; we still no-op when the step
 *     is already in completed_steps to avoid network chatter
 *   - silent on error: this is UI state, not data; we don't want to
 *     surface a failure to the user
 *   - "fire and forget": doesn't return a promise; the caller doesn't
 *     wait for the network round trip
 *
 * Usage:
 *   const markPeople = useOnboardingMark();
 *   useEffect(() => markPeople("people"), []);
 */
import { useCallback } from "react";

import {
  useGetOnboardingProgressQuery,
  useMarkOnboardingStepMutation,
} from "../api/onboardingApi";
import type { OnboardingStepKey } from "../model/types";

/** Hook: signal that an onboarding step is complete (idempotent, fire-and-forget). */
export function useOnboardingMark() {
  const { data: progress } = useGetOnboardingProgressQuery();
  const [markStep] = useMarkOnboardingStepMutation();

  return useCallback(
    (stepKey: OnboardingStepKey) => {
      // Skip if dismissed or already complete - no point hitting the BE
      if (!progress) return;
      if (progress.is_dismissed) return;
      if (progress.completed_steps.includes(stepKey)) return;
      void markStep({ step_key: stepKey });
    },
    [progress, markStep],
  );
}
